/**
 * Curated work index — product-grade case studies + selective labs.
 * Synthetic demo farm is intentionally excluded from Featured.
 */

export const workCategories = [
  { id: 'all', label: 'All' },
  { id: 'featured', label: 'Featured' },
  { id: 'client', label: 'Client' },
  { id: 'systems', label: 'Systems' },
  { id: 'labs', label: 'Labs' },
];

export const workItems = [
  {
    id: 'three-sisters-oyster-project',
    slug: 'three-sisters-oyster-project',
    title: 'Three Sisters Oyster Co.',
    subtitle: 'Production e-commerce for Gulf Coast aquaculture',
    blurb:
      'Full-stack storefront for a working oyster farm — catalog, inventory, checkout, and a mobile-first buying experience.',
    category: 'client',
    featured: true,
    tags: ['React', 'Node.js', 'Stripe', 'MongoDB'],
    href: 'https://threesistersoyster.com',
    github: 'cael1127/threesistersoyster',
    year: '2025',
    accent: '#A31F34',
  },
  {
    id: 'aquaFarm',
    slug: 'aquafarm',
    title: 'AquaFarm',
    subtitle: 'Aquaculture monitoring concepts',
    blurb:
      'TypeScript tooling around aquaculture monitoring, data workflows, and ops-friendly dashboards.',
    category: 'systems',
    featured: true,
    tags: ['TypeScript', 'IoT', 'Data'],
    github: 'cael1127/aquaFarm',
    year: '2025',
    accent: '#A31F34',
  },
  {
    id: 'boltPlanner',
    slug: 'boltplanner',
    title: 'BoltPlanner',
    subtitle: 'Planning tooling for clarity and speed',
    blurb: 'A planning product focused on organization, fast capture, and clean execution flows.',
    category: 'systems',
    featured: true,
    tags: ['TypeScript', 'Product', 'UX'],
    github: 'cael1127/boltPlanner',
    year: '2025',
    accent: '#A31F34',
  },
  {
    id: 'grabby',
    slug: 'grabby',
    title: 'Grabby',
    subtitle: 'Utility product experiments',
    blurb: 'JavaScript utility project exploring practical capture and workflow helpers.',
    category: 'systems',
    featured: true,
    tags: ['JavaScript', 'Tooling'],
    github: 'cael1127/grabby',
    year: '2025',
    accent: '#A31F34',
  },
  {
    id: 'neurals',
    slug: 'neurals',
    title: 'Neurals',
    subtitle: 'Systems-level neural building blocks',
    blurb: 'Rust experimentation in neural / compute primitives — depth over demo polish.',
    category: 'systems',
    featured: true,
    tags: ['Rust', 'Systems', 'AI'],
    github: 'cael1127/neurals',
    year: '2025',
    accent: '#A31F34',
  },
  {
    id: 'AtlusPersonal',
    slug: 'atlus',
    title: 'Atlus',
    subtitle: 'End-to-end product exploration',
    blurb: 'Personal product build covering design and implementation details from concept to UI.',
    category: 'systems',
    featured: true,
    tags: ['TypeScript', 'Full-stack'],
    github: 'cael1127/AtlusPersonal',
    year: '2025',
    accent: '#A31F34',
  },
  {
    id: 'aisw',
    slug: 'aisw',
    title: 'AISW',
    subtitle: 'Applied software experiments',
    blurb: 'JavaScript project exploring applied software patterns and interactive surfaces.',
    category: 'systems',
    featured: false,
    tags: ['JavaScript'],
    github: 'cael1127/aisw',
    year: '2025',
    accent: '#A31F34',
  },
  {
    id: 'physics',
    slug: 'physics',
    title: 'Physics',
    subtitle: 'Low-level simulation in C',
    blurb: 'Performance-oriented physics experiments and simulations written in C.',
    category: 'systems',
    featured: false,
    tags: ['C', 'Simulation'],
    github: 'cael1127/physics',
    year: '2025',
    accent: '#A31F34',
  },
  {
    id: 'terminalUI',
    slug: 'terminal-ui',
    title: 'Terminal UI',
    subtitle: 'Console UX with intent',
    blurb: 'Rust terminal UI focused on clean interactions inside the console.',
    category: 'systems',
    featured: false,
    tags: ['Rust', 'CLI', 'UX'],
    github: 'cael1127/terminalUI',
    year: '2025',
    accent: '#A31F34',
  },
  {
    id: 'bapux-project',
    slug: 'bapux-project',
    title: 'Bapux',
    subtitle: 'Client web experience',
    blurb: 'Shipped client site with clear information architecture and fast delivery.',
    category: 'client',
    featured: true,
    tags: ['React', 'Design'],
    year: '2024',
    accent: '#A31F34',
  },
  {
    id: 'bpawd-project',
    slug: 'bpawd-project',
    title: 'BPAWD',
    subtitle: 'Competition web platform',
    blurb: 'Web platform work for BPA team competition deliverables.',
    category: 'client',
    featured: false,
    tags: ['JavaScript', 'Web'],
    github: 'cael1127/BPAWDTeam',
    year: '2024',
    accent: '#A31F34',
  },
  {
    id: 'uil-academy-project',
    slug: 'uil-academy-project',
    title: 'UIL Academy',
    subtitle: 'Learning platform',
    blurb: 'Educational platform surface for competitive academics and CS training.',
    category: 'client',
    featured: true,
    tags: ['TypeScript', 'Education'],
    github: 'cael1127/UILCSAC',
    year: '2024',
    accent: '#A31F34',
  },
  {
    id: 'minbod-project',
    slug: 'minbod-project',
    title: 'MinBod',
    subtitle: 'Client product site',
    blurb: 'Focused client build with clean structure and responsive layout.',
    category: 'client',
    featured: false,
    tags: ['Web', 'UI'],
    year: '2024',
    accent: '#A31F34',
  },
  {
    id: 'jf-resume-project',
    slug: 'jf-resume-project',
    title: 'JF Portfolio',
    subtitle: 'Mechanical engineering portfolio',
    blurb: 'Portfolio site for mechanical engineering work — typography and clarity first.',
    category: 'client',
    featured: false,
    tags: ['JavaScript', 'Portfolio'],
    github: 'cael1127/JFPortfolio',
    year: '2024',
    accent: '#A31F34',
  },
  // Selective interactive labs (honest demos worth discussing)
  {
    id: 'object-detection-demo',
    slug: 'object-detection-demo',
    title: 'Object Detection Lab',
    subtitle: 'Deterministic overlay algorithm',
    blurb: 'Interactive lab for object detection overlays — approachable, inspectable logic.',
    category: 'labs',
    featured: false,
    tags: ['Computer Vision', 'React'],
    year: '2025',
    accent: '#A31F34',
    isLab: true,
  },
  {
    id: 'saas-analytics-demo',
    slug: 'saas-analytics-demo',
    title: 'SaaS Analytics Lab',
    subtitle: 'Cohort retention visualization',
    blurb: 'SVG-driven cohort retention charts as a compact analytics surface.',
    category: 'labs',
    featured: false,
    tags: ['Data Viz', 'React'],
    year: '2025',
    accent: '#A31F34',
    isLab: true,
  },
  {
    id: 'ecommerce-demo',
    slug: 'ecommerce-demo',
    title: 'Storefront Lab',
    subtitle: 'Catalog, cart, totals',
    blurb: 'Compact e-commerce interaction lab — catalog flow and cart math.',
    category: 'labs',
    featured: false,
    tags: ['React', 'Commerce'],
    year: '2025',
    accent: '#A31F34',
    isLab: true,
  },
  {
    id: 'realtime-chat-demo',
    slug: 'realtime-chat-demo',
    title: 'Realtime Chat Lab',
    subtitle: 'Presence via BroadcastChannel',
    blurb: 'Lightweight realtime presence and messaging without a heavy backend.',
    category: 'labs',
    featured: false,
    tags: ['Realtime', 'React'],
    year: '2025',
    accent: '#A31F34',
    isLab: true,
  },
];

export const featuredWork = workItems.filter((w) => w.featured);

export function getWorkById(id) {
  return workItems.find((w) => w.id === id || w.slug === id);
}

export function filterWork(category) {
  if (!category || category === 'all') return workItems;
  if (category === 'featured') return workItems.filter((w) => w.featured);
  return workItems.filter((w) => w.category === category);
}
